import { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { selectCurrentUser, setCurrentUser } from "../Users/usersSlice";
import { EULA } from "./EULA";
import { completeOnboarding } from "./inviteUtils";
import { fetchUsersByEmail } from "../Users/usersAPI";
import { User } from "../../API";
import { performSimplAuthSignOut } from "../Auth/signOut";
import { useOptionalSimplAuthCaller } from "../Auth/SimplAuthGuard";

/**
 * EulaGate component that blocks app access until EULA is accepted.
 * Shows EULA modal if user hasn't accepted EULA yet.
 * Calls complete-onboarding API on acceptance, which handles both
 * EULA acceptance and auto-assignment of assessments.
 */
export const EulaGate = ({ children }: { children: React.ReactNode }) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  // Email is sourced from the simpl-auth-authorizer caller (published
  // by `<SimplAuthGuard>`) and falls back to the redux currentUser
  // payload — both reflect the same authenticated identity but the
  // currentUser fallback keeps tests that don't wrap a guard
  // working.
  const callerCtx = useOptionalSimplAuthCaller();
  const callerEmail =
    callerCtx?.caller.email?.toLowerCase() ??
    currentUser?.email?.toLowerCase() ??
    null;

  const [showEula, setShowEula] = useState(false);
  const [isAcceptingEula, setIsAcceptingEula] = useState(false);
  const [eulaError, setEulaError] = useState<string | null>(null);
  const [isRefreshingUser, setIsRefreshingUser] = useState(false);

  useEffect(() => {
    if (currentUser && !currentUser.eulaAcceptedDate) {
      setShowEula(true);
    } else if (currentUser && currentUser.eulaAcceptedDate) {
      setShowEula(false);
    }
  }, [currentUser]);

  const handleEulaAccept = async () => {
    try {
      setEulaError(null);
      setIsAcceptingEula(true);

      await completeOnboarding();

      setIsAcceptingEula(false);
      setIsRefreshingUser(true);

      try {
        if (callerEmail) {
          const users = await fetchUsersByEmail(callerEmail);
          if (users && users.length > 0) {
            const updatedUser: User = users[0];
            dispatch(setCurrentUser(updatedUser));
            setShowEula(false);
          } else {
            throw new Error("User not found after EULA acceptance");
          }
        } else {
          throw new Error("Unable to determine user email");
        }
      } catch (refreshError) {
        console.error("Failed to refresh user data:", refreshError);
        setEulaError(
          "EULA accepted, but unable to refresh user data. Please refresh the page."
        );
      } finally {
        setIsRefreshingUser(false);
      }
    } catch (error: any) {
      console.error("Error in handleEulaAccept:", error);
      setIsAcceptingEula(false);
      setIsRefreshingUser(false);

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Unable to record EULA acceptance. Please try again.";
      setEulaError(errorMessage);
    }
  };

  const handleEulaCancel = async () => {
    await performSimplAuthSignOut({ dispatch });
  };

  if (currentUser && !currentUser.eulaAcceptedDate) {
    return (
      <>
        <EULA
          open={showEula}
          setOpen={() => {}}
          loading={isAcceptingEula || isRefreshingUser}
          onAccept={handleEulaAccept}
          allowCancel={true}
          errorMessage={eulaError}
          onCancel={handleEulaCancel}
        />
      </>
    );
  }

  return <>{children}</>;
};
